import { deepClone } from '../utils/object';
import { unique } from '../utils/array';

const initialState = {
	categories: [],
	brands: [],
	discounts: [],
	colors: [],
	sizes: [],
	newProducts: false,
	productsOnSale: false,
	filteredBy: [],
	currentFilterSegment: '', // Specific brand or top category.
	clearFilterBase: [], // Clicked link set when force used. Should be updated if removed in filter. Women / Sale clicked -> Sale removed in filter. Women only remains in clearFilterBase.
	sort: '',
};

const updateFilteredBy = (state, newFilterKey, newFilter) => {
	if ((typeof newFilter[newFilterKey] === 'boolean' && newFilter[newFilterKey])) {
		return [...state, newFilter];
	}

	let removeIndex = false;
	let updated = false;

	// Check if value exists -> update
	const tempFilter = state.map((currentFilter, i) => {
		const prevFilterKey = Object.keys(currentFilter)[0];
		if (newFilterKey === prevFilterKey) {
			if ((Array.isArray(newFilter[newFilterKey]) && newFilter[newFilterKey].length === 0) || newFilter[newFilterKey] === false) {
				removeIndex = i;
			}
			updated = true;
			return newFilter;
		}
		return currentFilter;
	});

	// Remove
	if (removeIndex !== false) {
		return [...state.slice(0, removeIndex), ...state.slice(removeIndex + 1)];
	}

	// Add
	if (updated === false) {
		return [...state, newFilter];
	}

	// Update
	return tempFilter;
};

const updateFilterSegment = (state, filterKey, filterValue = '') => {
	if (state.currentFilterSegment === '' && (filterKey === 'categories' || filterKey === 'brands' || filterKey === 'productsOnSale' || filterKey === 'newProducts')) {
		state = {
			...state,
			currentFilterSegment: filterKey === 'categories' ? filterValue.split('/')[0] : 'all',
		};
	}

	return state;
};

const updateFilterState = (state, filterKey, filterValue, force) => {
	let updatedValues = [];

	if (force && !state[filterKey]) return state;

	if (force && state[filterKey].indexOf(filterKey) !== -1) {
		const value = state[filterKey];
		updatedValues = unique([...value.slice(value.indexOf(filterValue), value.indexOf(filterValue) + 1), ...value.slice(0, value.indexOf(filterValue)), ...value.slice(value.indexOf(filterValue) + 1)]); // Move value to front.
	} else if (!force && state[filterKey].indexOf(filterValue) !== -1) {
		updatedValues = unique([...state[filterKey].slice(0, state[filterKey].indexOf(filterValue)), ...state[filterKey].slice(state[filterKey].indexOf(filterValue) + 1)]); // Remove
	} else if (force) {
		updatedValues = unique([filterValue, ...state[filterKey]]); // Add value
	} else {
		updatedValues = unique([...state[filterKey], filterValue]); // Add value
	}

	state = {
		...state,
		[filterKey]: updatedValues,
		filteredBy: updateFilteredBy(state.filteredBy, filterKey, { [filterKey]: [...updatedValues] }),
		clearFilterBase: force ? updateFilteredBy(state.clearFilterBase, filterKey, { [filterKey]: [...updatedValues] }) : [...state.clearFilterBase],
	};

	state = { ...updateFilterSegment(state, filterKey, filterValue) };

	return state;
};

const filterNextState = (state, action) => {
	let nextState = deepClone(state);

	// [{ filterKey: filterValue ([], '') }, { ... }, ...]

	action.filterArray.forEach((filter) => {
		const filterKey = Object.keys(filter)[0];
		let filterValueRaw = filter[filterKey];

		if (filterKey === 'categories') {
			if (!Array.isArray(filterValueRaw)) filterValueRaw = [filterValueRaw];

			filterValueRaw.forEach((filterValue) => {
				const categoryParts = filterValue.split('/');
				if (categoryParts[0] !== nextState.currentFilterSegment && nextState.currentFilterSegment !== 'all') {
					/*
						// Unused due to running "RESET_FILTER" first
						// If categories have different bases then remove all filters before applying new.
						kids/shoes,kids/clothing/gifts -> men/* => men/* (clears filter as we now have a new base category)
					*/
					// Clear previous state and just update filteredBy as per normal.
					nextState = deepClone(initialState);
					nextState = {
						...nextState,
						currentFilterSegment: categoryParts[0],
					};
					nextState = updateFilterState(nextState, filterKey, filterValue, action.force);
				} else if (nextState.categories.length === 1 && nextState.categories.indexOf(filterValue) !== -1 && !action.force) {
					/*
						// Removing final category.
						kids/clothing/gifts -> kids/clothing/gifts => kids/clothing (moved up one level - can't have no categories unless there is another base)
					*/
					const newValue = categoryParts.length === 1 ? [filterValue] : [categoryParts.slice(0, -1).join('/')];

					if (nextState.currentFilterSegment === 'all' && categoryParts.length === 1) {
						nextState = {
							...nextState,
							[filterKey]: [],
							filteredBy: updateFilteredBy(nextState.filteredBy, filterKey, { [filterKey]: [] }),
							clearFilterBase: updateFilteredBy(nextState.clearFilterBase, filterKey, { [filterKey]: [] }),
						};
					} else {
						nextState = {
							...nextState,
							[filterKey]: newValue,
							filteredBy: updateFilteredBy(nextState.filteredBy, filterKey, { [filterKey]: [...newValue] }),
							clearFilterBase: updateFilteredBy(nextState.clearFilterBase, filterKey, { [filterKey]: [...newValue] }),
						};
					}
				} else if (nextState.categories.length > 0) {
					/*
						// Filter when choosing deeper categories i.e.
						kids -> kids/clothing/gifts => kids/clothing/gifts (removes 'kids')
						kids/shoes,kids/clothing -> kids/clothing/gifts => kids/shoes,kids/clothing/gifts (removes 'kids/clothing')

						// Parent category added remove superfluous deeper categories
						kids/shoes,kids/clothing -> kids => kids (removes kids/shoes and kids/clothing as they exist in 'kids')
					*/

					const newValues = nextState.categories.filter((previousCategory) => {
						// if categories are exactly the same then remove them.
						if (previousCategory === filterValue && !action.force) return false;
						if (previousCategory === filterValue && action.force) return true;

						const previousCategoryParts = previousCategory.split('/');

						// if previous categories are more general than filterValues and have same base. Remove the more generic categories.
						if (previousCategoryParts.length < categoryParts.length) {
							// check if they have same base.
							let hasSameBase = true;
							categoryParts.slice(0, -1).forEach((part, i) => {
								if (previousCategoryParts[i] !== undefined && previousCategoryParts[i] !== part) hasSameBase = false;
							});

							// Has same base previous category is less specific than new category. Remove the old category.
							if (hasSameBase) return false;
						}

						// if previous categories are more specific than filterValue and have same base. Remove the more specific categories.
						if (previousCategoryParts.length > categoryParts.length) {
							// check if they have same base.
							let hasSameBase = true;
							categoryParts.forEach((part, i) => {
								if (previousCategoryParts[i] !== part) hasSameBase = false;
							});

							// Has same base previous category is more specific than new category. Remove the old category.
							if (hasSameBase) return false;
						}

						return true;
					});

					let newCategories = [];

					if (action.force) {
						newCategories = nextState.categories.indexOf(filterValue) === -1 ? [filterValue, ...newValues] : [...newValues.slice(newValues.indexOf(filterValue), newValues.indexOf(filterValue) + 1), ...newValues.slice(0, newValues.indexOf(filterValue)), ...newValues.slice(newValues.indexOf(filterValue) + 1)]; // Move value to front.
					} else {
						newCategories = nextState.categories.indexOf(filterValue) === -1 ? [...newValues, filterValue] : [...newValues];
					}

					nextState = {
						...nextState,
						[filterKey]: newCategories,
						filteredBy: updateFilteredBy(nextState.filteredBy, filterKey, { [filterKey]: [...newCategories] }),
					};
				} else {
					nextState = updateFilterState(nextState, filterKey, filterValue, action.force);
				}
			});
		} else if (typeof nextState[filterKey] === 'boolean') {
			// filterValueRaw is only string for boolean.
			nextState = updateFilterSegment(nextState, filterKey);

			// Special functionality to remove discounts from filter when "parent" productsOnSale is removed.
			const hasDiscounts = filterKey === 'productsOnSale' && nextState[filterKey] && state.discounts.length > 0;
			const discounts = hasDiscounts ? [] : [...state.discounts];
			let filteredBy = updateFilteredBy(nextState.filteredBy, filterKey, { [filterKey]: action.force ? true : !nextState[filterKey] });
			filteredBy = filteredBy.filter((f) => Object.keys(f)[0] !== 'discounts');

			nextState = {
				...nextState,
				discounts,
				[filterKey]: action.force ? true : !nextState[filterKey],
				filteredBy,
				clearFilterBase: action.force ? updateFilteredBy(nextState.clearFilterBase, filterKey, { [filterKey]: action.force ? true : !nextState[filterKey] }) : [...state.clearFilterBase],
			};
		} else if (filterKey === 'sort') {
			// filterValueRaw is only string for sort.
			nextState = {
				...nextState,
				sort: filterValueRaw,
			};
		} else {
			if (!Array.isArray(filterValueRaw)) filterValueRaw = [filterValueRaw];

			filterValueRaw.forEach((filterValue) => {
				nextState = updateFilterState(nextState, filterKey, filterValue, action.force);
			});
		}
	});

	return nextState;
};

export default (state = deepClone(initialState), action = {}) => {
	if (action.type === 'FILTER') {
		return filterNextState(state, action);
	}
	if (action.type === 'CLEAR_FILTER') {
		action.force = true;
		action.filterArray = deepClone(state.clearFilterBase);
		return filterNextState(deepClone(initialState), action);
	}
	if (action.type === 'RESET_FILTER') {
		return deepClone(initialState);
	}

	return state;
};
